import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X, RotateCcw, MapPin, AlertTriangle } from "lucide-react";

/* ================================================================
   OnboardingTour — truly interactive step-by-step walkthrough.
   Clicks buttons, activates edit mode, waits for elements, and
   spotlights real UI components.

   localStorage persistence: the tour auto-shows once for new users.
   After the user completes OR dismisses it, a flag is saved so it
   won't auto-open again. The HelpDrawer "Start Tour" button always
   works regardless.
   ================================================================ */

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

/* ---------- localStorage persistence ---------- */

const TOUR_STORAGE_KEY = "zap-onboarding-tour-completed";

/** Check if the user has already completed or dismissed the tour. */
export function isTourCompleted(): boolean {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/** Mark the tour as completed (won't auto-show again). */
export function markTourCompleted(): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  } catch {
    /* localStorage unavailable — ignore */
  }
}

/** Reset the tour flag so it auto-shows again on next visit. */
export function resetTourCompleted(): void {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/* ---------- Helpers ---------- */

/** Poll the DOM until selector matches, or timeout. */
function waitForElement(selector: string, timeoutMs = 2000): Promise<Element | null> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) { resolve(el); return; }
    const start = Date.now();
    const interval = setInterval(() => {
      const found = document.querySelector(selector);
      if (found) { clearInterval(interval); resolve(found); return; }
      if (Date.now() - start > timeoutMs) { clearInterval(interval); resolve(null); }
    }, 80);
  });
}

/** Click an element found by selector. */
function clickElement(selector: string) {
  const el = document.querySelector(selector) as HTMLElement | null;
  if (el) el.click();
}

/** Scroll element into view smoothly. */
function scrollIntoViewIfNeeded(el: Element) {
  const rect = el.getBoundingClientRect();
  const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
  if (!inView) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

/* ---------- Step definitions ---------- */

interface TourStep {
  id: string;
  target: string;
  title: string;
  body: string;
  position: "top" | "bottom" | "left" | "right";
  /** Async action to run BEFORE showing this step (e.g. click edit button). */
  beforeStep?: () => Promise<void>;
  /** Async action to run when LEAVING this step (cleanup). */
  afterStep?: () => Promise<void>;
}

function buildSteps(): TourStep[] {
  return [
    {
      id: "welcome",
      target: '[data-tour-step="admin-bar"]',
      title: "\u05D1\u05E8\u05D5\u05DB\u05D9\u05DD \u05D4\u05D1\u05D0\u05D9\u05DD \u05DC\u05E1\u05D9\u05D5\u05E8!",
      body: "\u05D6\u05D4 \u05E1\u05E8\u05D2\u05DC \u05D4\u05E0\u05D9\u05D4\u05D5\u05DC \u2014 \u05DE\u05DB\u05D0\u05DF \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D7\u05D6\u05D5\u05E8 \u05DC\u05DC\u05D5\u05D7 \u05D4\u05D1\u05E7\u05E8\u05D4, \u05DC\u05D2\u05E9\u05EA \u05DC\u05E2\u05E5 \u05D0\u05EA\u05E8, \u05DC\u05E0\u05D4\u05DC SEO \u05D5\u05E2\u05D5\u05D3. \u05E0\u05EA\u05D7\u05D9\u05DC?",
      position: "bottom",
    },
    {
      id: "edit-toggle",
      target: '[data-tour-step="edit-toggle"]',
      title: "\u05DB\u05E0\u05D9\u05E1\u05D4 \u05DC\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4",
      body: "\u05D6\u05D4 \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4 \u05D4\u05E8\u05D0\u05E9\u05D9. \u05DC\u05D7\u05D9\u05E6\u05D4 \u05E2\u05DC\u05D9\u05D5 \u05DE\u05E4\u05E2\u05D9\u05DC\u05D4 \u05D0\u05EA \u05DE\u05E6\u05D1 \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4 \u05E9\u05DC \u05D4\u05D0\u05EA\u05E8 \u05DB\u05D5\u05DC\u05D5. \u05D0\u05E4\u05E9\u05E8 \u05D2\u05DD \u05E2\u05DD Ctrl+E.",
      position: "right",
      beforeStep: async () => {
        // Make sure we're NOT in edit mode yet (so user sees the pencil)
        const toolbar = document.querySelector('[data-tour-step="edit-toolbar"]');
        if (toolbar) {
          // Already in edit mode — exit first
          clickElement('[data-tour-step="edit-toggle"]');
          await waitForElement('[data-tour-step="edit-toggle"]:not([style*="background-color: rgb(245, 158, 11)"])');
          await new Promise((r) => setTimeout(r, 400));
        }
      },
    },
    {
      id: "edit-toolbar",
      target: '[data-tour-step="edit-toolbar"]',
      title: "\u05E1\u05E8\u05D2\u05DC \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4",
      body: "\u05D0\u05D7\u05E8\u05D9 \u05D4\u05E4\u05E2\u05DC\u05D4, \u05E1\u05E8\u05D2\u05DC \u05D4\u05E2\u05E8\u05D9\u05DB\u05D4 \u05DE\u05D5\u05E4\u05D9\u05E2 \u05DC\u05DE\u05E2\u05DC\u05D4 \u2014 \u05E1\u05D8\u05D8\u05D5\u05E1 \u05E9\u05DE\u05D9\u05E8\u05D4, \u05D1\u05D9\u05D8\u05D5\u05DC/\u05E9\u05D7\u05D6\u05D5\u05E8, \u05E2\u05D5\u05D6\u05E8 AI, \u05E0\u05D9\u05D4\u05D5\u05DC \u05EA\u05E4\u05E8\u05D9\u05D8, \u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4 \u05D5\u05E2\u05D5\u05D3.",
      position: "bottom",
      beforeStep: async () => {
        // Enter edit mode
        const toolbar = document.querySelector('[data-tour-step="edit-toolbar"]');
        if (!toolbar) {
          clickElement('[data-tour-step="edit-toggle"]');
          await waitForElement('[data-tour-step="edit-toolbar"]', 3000);
          await new Promise((r) => setTimeout(r, 400));
        }
      },
    },
    {
      id: "inline-edit",
      target: '[data-tour-step="inline-edit"]',
      title: "\u05E2\u05E8\u05D9\u05DB\u05EA \u05D8\u05E7\u05E1\u05D8 \u05D9\u05E9\u05D9\u05E8\u05D4",
      body: "\u05E8\u05D5\u05D0\u05D9\u05DD \u05D0\u05EA \u05D4\u05DE\u05E1\u05D2\u05E8\u05EA \u05D4\u05E6\u05D1\u05E2\u05D5\u05E0\u05D9\u05EA? \u05D1\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4, \u05DB\u05DC \u05D8\u05E7\u05E1\u05D8 \u05D1\u05D0\u05EA\u05E8 \u05D4\u05D5\u05E4\u05DA \u05DC\u05E9\u05D3\u05D4 \u05E7\u05DC\u05D9\u05E7 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC\u05D9\u05D5, \u05E9\u05E0\u05D5, \u05D5\u05D4\u05E9\u05D9\u05E0\u05D5\u05D9 \u05E0\u05E9\u05DE\u05E8 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA.",
      position: "bottom",
      beforeStep: async () => {
        // Scroll up to the hero title area
        window.scrollTo({ top: 0, behavior: "smooth" });
        await new Promise((r) => setTimeout(r, 500));
      },
    },
    {
      id: "palette-btn",
      target: '[data-tour-step="palette-btn"]',
      title: "\u05D1\u05E0\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD",
      body: "\u05DB\u05D0\u05DF \u05D0\u05E4\u05E9\u05E8 \u05DC\u05E4\u05EA\u05D5\u05D7 \u05D0\u05EA \u05D1\u05E0\u05E7 \u05D4\u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD \u2014 \u05D2\u05E8\u05E8\u05D5 \u05E8\u05DB\u05D9\u05D1\u05D9\u05DD \u05DE\u05D4\u05E8\u05E9\u05D9\u05DE\u05D4 \u05DC\u05EA\u05D5\u05DA \u05D4\u05E2\u05DE\u05D5\u05D3. \u05DB\u05D5\u05EA\u05E8\u05D5\u05EA, \u05D8\u05E7\u05E1\u05D8\u05D9\u05DD, \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA, \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05E9\u05DC\u05DE\u05D9\u05DD \u05D5\u05E2\u05D5\u05D3.",
      position: "right",
    },
    {
      id: "comment-toggle",
      target: '[data-tour-step="comment-toggle"]',
      title: "\u05DE\u05E2\u05E8\u05DB\u05EA \u05D4\u05E2\u05E8\u05D5\u05EA",
      body: "\u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05D1\u05D5\u05E2\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05E4\u05E2\u05D9\u05DC \u05DE\u05E6\u05D1 \u05D4\u05E2\u05E8\u05D5\u05EA. \u05DC\u05D7\u05E6\u05D5 \u05E2\u05DC \u05DB\u05DC \u05DE\u05E7\u05D5\u05DD \u05D1\u05E2\u05DE\u05D5\u05D3 \u05DC\u05D4\u05E6\u05DE\u05D9\u05D3 \u05E4\u05D9\u05DF, \u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05EA\u05D2\u05D5\u05D1\u05D5\u05EA \u05DE\u05E7\u05D5\u05E0\u05E0\u05D5\u05EA, \u05EA\u05D9\u05D9\u05D2\u05D5 \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD \u05E2\u05DD @ \u05D5\u05D2\u05E8\u05E8\u05D5 \u05E4\u05D9\u05E0\u05D9\u05DD.",
      position: "top",
    },
    {
      id: "notification-bell",
      target: '[data-tour-step="notification-bell"]',
      title: "\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D1\u05D6\u05DE\u05DF \u05D0\u05DE\u05EA",
      body: "\u05D4\u05E4\u05E2\u05DE\u05D5\u05DF \u05DE\u05E8\u05D0\u05D4 \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D7\u05D3\u05E9\u05D5\u05EA \u2014 \u05D4\u05E2\u05E8\u05D5\u05EA, \u05EA\u05D9\u05D5\u05D2\u05D9\u05DD \u05D5\u05E2\u05D3\u05DB\u05D5\u05E0\u05D9\u05DD. \u05DC\u05D7\u05D9\u05E6\u05D4 \u05E2\u05DC \u05D4\u05EA\u05E8\u05D0\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05E0\u05D5\u05D5\u05D8 \u05D9\u05E9\u05D9\u05E8\u05D5\u05EA \u05DC\u05D4\u05E2\u05E8\u05D4 \u05D4\u05E8\u05DC\u05D5\u05D5\u05E0\u05D8\u05D9\u05EA.",
      position: "bottom",
    },
    {
      id: "nav-editor-btn",
      target: '[data-tour-step="nav-editor-btn"]',
      title: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05EA\u05E4\u05E8\u05D9\u05D8 \u05E0\u05D9\u05D5\u05D5\u05D8",
      body: "\u05DE\u05DB\u05D0\u05DF \u05D0\u05E4\u05E9\u05E8 \u05DC\u05E0\u05D4\u05DC \u05D0\u05EA \u05EA\u05E4\u05E8\u05D9\u05D8 \u05D4\u05E0\u05D9\u05D5\u05D5\u05D8 \u05E9\u05DC \u05D4\u05D0\u05EA\u05E8: \u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD, \u05E9\u05E0\u05D5 \u05E9\u05DE\u05D5\u05EA, \u05E1\u05D3\u05E8\u05D5 \u05DE\u05D7\u05D3\u05E9, \u05D4\u05E1\u05EA\u05D9\u05E8\u05D5 \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD \u05D5\u05E0\u05D4\u05DC\u05D5 \u05EA\u05EA-\u05E2\u05DE\u05D5\u05D3\u05D9\u05DD.",
      position: "right",
    },
    {
      id: "help-btn",
      target: '[data-tour-step="help-btn"]',
      title: "\u05E4\u05D0\u05E0\u05DC \u05E2\u05D6\u05E8\u05D4",
      body: "\u05DB\u05E4\u05EA\u05D5\u05E8 \u05D4\u05E2\u05D6\u05E8\u05D4 \u05D6\u05DE\u05D9\u05DF \u05EA\u05DE\u05D9\u05D3 \u2014 \u05D4\u05E1\u05D1\u05E8\u05D9\u05DD, \u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05DC\u05D3\u05EA, \u05D5\u05D4\u05D0\u05E4\u05E9\u05E8\u05D5\u05EA \u05DC\u05D4\u05E4\u05E2\u05D9\u05DC \u05D0\u05EA \u05D4\u05E1\u05D9\u05D5\u05E8 \u05D4\u05D6\u05D4 \u05DE\u05D7\u05D3\u05E9 \u05D1\u05DB\u05DC \u05E8\u05D2\u05E2.",
      position: "left",
      afterStep: async () => {
        // Tour done — leave edit mode on so user can explore
      },
    },
  ];
}

/* ---------- Spotlight + tooltip positioning ---------- */

interface Rect { top: number; left: number; width: number; height: number; }

function computeTooltip(
  spotRect: Rect,
  position: TourStep["position"],
  tooltipW: number,
  pad: number,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = spotRect.left + spotRect.width / 2;

  let top = 0;
  let left = 0;

  switch (position) {
    case "bottom":
      top = spotRect.top + spotRect.height + pad + 16;
      left = cx - tooltipW / 2;
      break;
    case "top":
      top = spotRect.top - pad - 16 - 220;
      left = cx - tooltipW / 2;
      break;
    case "left":
      top = spotRect.top;
      left = spotRect.left - pad - 16 - tooltipW;
      break;
    case "right":
    default:
      top = spotRect.top;
      left = spotRect.left + spotRect.width + pad + 16;
      break;
  }

  // Clamp
  if (left < 16) left = 16;
  if (left + tooltipW > vw - 16) left = vw - 16 - tooltipW;
  if (top < 16) top = 16;
  if (top + 260 > vh - 16) top = vh - 16 - 260;

  return { top, left };
}

/* ---------- Component ---------- */

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
  /** User role — restricted roles get a shorter tour (3 steps) */
  userRole?: string;
}

export function OnboardingTour({ isOpen, onClose, userRole = "admin" }: OnboardingTourProps) {
  const [stepIdx, setStepIdx] = useState(0);
  const [spotRect, setSpotRect] = useState<Rect | null>(null);
  const [loading, setLoading] = useState(false);
  const [noTarget, setNoTarget] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const stepsRef = useRef<TourStep[]>([]);

  // Initialize steps once (filtered by role)
  useEffect(() => {
    const all = buildSteps();
    const restricted = ["designer", "content_writer", "developer", "marketer", "client_manager", "onboarding"];
    if (restricted.includes(userRole)) {
      // Restricted roles only see: welcome, inline-edit (read-only explanation), help-btn
      stepsRef.current = all.filter((s) =>
        s.id === "welcome" || s.id === "inline-edit" || s.id === "help-btn"
      );
    } else if (userRole === "characterizer") {
      // Characterizer skips admin-only steps but keeps editing ones
      stepsRef.current = all.filter((s) =>
        s.id !== "nav-editor-btn"
      );
    } else {
      stepsRef.current = all;
    }
  }, [userRole]);

  const steps = stepsRef.current;
  const total = steps.length;
  const current = steps[stepIdx];

  // Reset when tour opens
  useEffect(() => {
    if (isOpen) {
      const all = buildSteps();
      const restricted = ["designer", "content_writer", "developer", "marketer", "client_manager", "onboarding"];
      if (restricted.includes(userRole)) {
        stepsRef.current = all.filter((s) =>
          s.id === "welcome" || s.id === "inline-edit" || s.id === "help-btn"
        );
      } else if (userRole === "characterizer") {
        // Characterizer skips admin-only steps but keeps editing ones
        stepsRef.current = all.filter((s) =>
          s.id !== "nav-editor-btn"
        );
      } else {
        stepsRef.current = all;
      }
      setStepIdx(0);
      setSpotRect(null);
      setNoTarget(false);
    }
  }, [isOpen, userRole]);

  // Continuously track the target element position
  const trackTarget = useCallback(() => {
    if (!isOpen || !current) return;
    const el = document.querySelector(current.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    rafRef.current = requestAnimationFrame(trackTarget);
  }, [isOpen, current]);

  // Run beforeStep + start tracking on step change
  useEffect(() => {
    if (!isOpen || !current) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setSpotRect(null);
      setNoTarget(false);

      // Run beforeStep action
      if (current.beforeStep) {
        try { await current.beforeStep(); } catch (_e) { /* ignore */ }
      }
      if (cancelled) return;

      // Wait for target element
      const el = await waitForElement(current.target, 3000);
      if (cancelled) return;

      if (el) {
        scrollIntoViewIfNeeded(el);
        await new Promise((r) => setTimeout(r, 350));
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        setNoTarget(false);
      } else {
        setSpotRect(null);
        setNoTarget(true);
      }
      setLoading(false);
    };

    run();

    // Start RAF tracking after initial setup
    const trackTimer = setTimeout(() => {
      if (!cancelled) {
        rafRef.current = requestAnimationFrame(trackTarget);
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(trackTimer);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, stepIdx, current, trackTarget]);

  // Navigate to step
  const goTo = useCallback(async (idx: number) => {
    if (idx < 0 || idx >= total) return;
    // Run afterStep of current
    const cur = stepsRef.current[stepIdx];
    if (cur?.afterStep) {
      try { await cur.afterStep(); } catch (_e) { /* ignore */ }
    }
    setStepIdx(idx);
  }, [stepIdx, total]);

  const handleClose = useCallback(async () => {
    const cur = stepsRef.current[stepIdx];
    if (cur?.afterStep) {
      try { await cur.afterStep(); } catch (_e) { /* ignore */ }
    }
    // Mark tour as completed/dismissed — won't auto-show again
    markTourCompleted();
    onClose();
  }, [onClose, stepIdx]);

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); handleClose(); }
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); goTo(stepIdx + 1); }
      if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); goTo(stepIdx - 1); }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [isOpen, handleClose, goTo, stepIdx]);

  if (!isOpen || !current) return null;

  const pad = 10;
  const tooltipW = 360;
  const hasSpotlight = spotRect !== null;

  const tooltipPos = hasSpotlight
    ? computeTooltip(spotRect, current.position, tooltipW, pad)
    : { top: window.innerHeight / 2 - 120, left: window.innerWidth / 2 - tooltipW / 2 };

  const isFirst = stepIdx === 0;
  const isLast = stepIdx === total - 1;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[10000]"
      dir="rtl"
      style={{ pointerEvents: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {/* SVG overlay with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            {/* White = visible (dark overlay), Black = cut-out (transparent) */}
            <rect width="100%" height="100%" fill="white" />
            {hasSpotlight && (
              <rect
                x={spotRect.left - pad}
                y={spotRect.top - pad}
                width={spotRect.width + pad * 2}
                height={spotRect.height + pad * 2}
                rx={12}
                ry={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(3,29,78,0.72)"
          mask="url(#tour-spotlight-mask)"
          style={{ pointerEvents: "auto" }}
        />
      </svg>

      {/* Highlight border around target */}
      {hasSpotlight && (
        <div
          className="absolute rounded-xl pointer-events-none"
          style={{
            top: spotRect.top - pad,
            left: spotRect.left - pad,
            width: spotRect.width + pad * 2,
            height: spotRect.height + pad * 2,
            border: "2px solid rgba(251,191,36,0.8)",
            boxShadow: "0 0 20px 4px rgba(251,191,36,0.25)",
            zIndex: 10001,
          }}
        />
      )}

      {/* Pulsing dot on spotlight */}
      {hasSpotlight && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: spotRect.top - pad - 6,
            left: spotRect.left - pad - 6,
            zIndex: 10001,
          }}
        >
          <span
            className="block w-3 h-3 rounded-full animate-ping"
            style={{ background: "#fbbf24", opacity: 0.75 }}
          />
          <span
            className="absolute inset-0 block w-3 h-3 rounded-full"
            style={{ background: "#f59e0b" }}
          />
        </div>
      )}

      {/* Tooltip card */}
      <div
        key={current.id}
        className="absolute bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: tooltipW,
          zIndex: 10002,
          animation: "tourFadeIn 0.25s ease-out",
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{
            background: "linear-gradient(135deg, #031d4e 0%, #0a2d6b 100%)",
            borderBottom: "3px solid #f59e0b",
          }}
        >
          <div className="flex items-center gap-2.5">
            <MapPin className="w-4 h-4" style={{ color: "#fbbf24" }} />
            <span className="text-white text-sm" style={{ fontWeight: 700 }}>
              {current.title}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className={`${btnReset} text-white/50 hover:text-white`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-3 py-2">
              <div className="w-5 h-5 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
              <span className="text-sm text-gray-400">{"\u05DE\u05DB\u05D9\u05DF \u05D0\u05EA \u05D4\u05E9\u05DC\u05D1 \u05D4\u05D1\u05D0..."}</span>
            </div>
          ) : noTarget ? (
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#d97706" }} />
              <div>
                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                  {current.body}
                </p>
                <p className="text-xs text-amber-600 leading-relaxed">
                  {"\u05D4\u05D0\u05DC\u05DE\u05E0\u05D8 \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0 \u05DB\u05E8\u05D2\u05E2 \u2014 \u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D0\u05EA\u05D4 \u05E6\u05E8\u05D9\u05DA \u05DC\u05D4\u05D9\u05D5\u05EA \u05D1\u05EA\u05D5\u05DA \u05E2\u05DE\u05D5\u05D3 \u05EA\u05D1\u05E0\u05D9\u05EA \u05DB\u05D3\u05D9 \u05DC\u05E8\u05D0\u05D5\u05EA \u05D0\u05D5\u05EA\u05D5."}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 text-sm leading-relaxed">
              {current.body}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex items-center justify-between border-t border-gray-100"
          style={{ background: "#f9fafb" }}
        >
          {/* Step counter */}
          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <span
                key={i}
                className="block rounded-full"
                style={{
                  width: i === stepIdx ? 20 : 8,
                  height: 8,
                  background: i === stepIdx ? "#f59e0b" : i < stepIdx ? "#fbbf24" : "#e5e7eb",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => goTo(stepIdx - 1)}
                disabled={loading}
                className={`${btnReset} flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs`}
                style={{ fontWeight: 600, color: "#374151", background: "#e5e7eb" }}
              >
                <ChevronRight className="w-3.5 h-3.5" />
                {"\u05D4\u05E7\u05D5\u05D3\u05DD"}
              </button>
            )}
            {!isLast ? (
              <button
                type="button"
                onClick={() => goTo(stepIdx + 1)}
                disabled={loading}
                className={`${btnReset} flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs shadow-sm`}
                style={{ fontWeight: 700, background: "#f59e0b", color: "#fff" }}
              >
                {"\u05D4\u05D1\u05D0"}
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleClose}
                className={`${btnReset} flex items-center gap-1 px-5 py-2 rounded-lg text-xs shadow-sm`}
                style={{
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  color: "#fff",
                }}
              >
                {"\u05E1\u05D9\u05D9\u05DE\u05EA\u05D9! \u05D1\u05D5\u05D0\u05D5 \u05E0\u05E2\u05E8\u05D5\u05DA \u2728"}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full rounded-full"
            style={{
              width: `${((stepIdx + 1) / total) * 100}%`,
              background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
              transition: "width 0.4s ease",
            }}
          />
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes tourFadeIn {
          from { opacity: 0; transform: translateY(12px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ---------- Re-start button for use in HelpDrawer ---------- */

export function StartTourButton({
  onClick,
  compact,
}: {
  onClick: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${btnReset} flex items-center gap-1.5 text-xs`}
        style={{ fontWeight: 600, color: "#4f46e5" }}
      >
        <RotateCcw className="w-3.5 h-3.5" />
        {"\u05D4\u05E4\u05E2\u05DC \u05E1\u05D9\u05D5\u05E8 \u05DE\u05D7\u05D3\u05E9"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${btnReset} flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-sm shadow-sm hover:shadow-md transition-shadow`}
      style={{
        fontWeight: 600,
        background: "linear-gradient(135deg, #031d4e 0%, #0a2d6b 100%)",
        color: "#fff",
        border: "2px solid rgba(245,158,11,0.4)",
      }}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "rgba(245,158,11,0.2)" }}
      >
        <MapPin className="w-4 h-4" style={{ color: "#fbbf24" }} />
      </span>
      <span className="flex flex-col items-start gap-0.5">
        <span>{"\u05D4\u05E4\u05E2\u05DC\u05EA \u05E1\u05D9\u05D5\u05E8 \u05D0\u05D9\u05E0\u05D8\u05E8\u05D0\u05E7\u05D8\u05D9\u05D1\u05D9"}</span>
        <span className="text-[11px] text-white/50" style={{ fontWeight: 400 }}>
          {"9 \u05E6\u05E2\u05D3\u05D9\u05DD \u00B7 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D4\u05EA\u05D7\u05DC\u05D4"}
        </span>
      </span>
    </button>
  );
}